import { apiFetch } from "./api";
import type { Client } from "./client.service";
import type { Company } from "./company.service";

export interface User {
  id: string;
  active: boolean;
  name: string;
  email: string;
  phone: string | null;
  role: UserRoles;
  codigo_erp?: string | null;
  // Conta de serviço: autentica só com token de API, nunca entra na UI.
  is_service_account?: boolean;
  loggedClient?: Client
}

export enum UserRoles {
  Admin = "admin",
  Cliente = "cliente",
  Operador = "operador",
}

export interface UserCompany {
  id: string,
  user_id: string,
  company_id: string
}

export interface UserCompanyModule {
  id: string,
  user_company_id: string,
  module_id: string
}

export const UserRoleLabels: Record<UserRoles, string> = {
  [UserRoles.Admin]: "Admin",
  [UserRoles.Cliente]: "Cliente",
  [UserRoles.Operador]: "Operador",
};

export function parseUserRole(role: string): UserRoles {
  if (Object.values(UserRoles).includes(role as UserRoles)) {
    return role as UserRoles;
  }

  console.warn("Cargo desconhecido recebido da API:", role);

  return UserRoles.Operador;
}

export type CreateUserData = Omit<User, 'id'> & { password?: string };

export type CreateUserCompanyData = {
  user_id: string,
  company_id: string
}

export type UserCompanyModuleData = {
  user_id: string,
  company_id: string,
  module_id: string
}

export type ResetPasswordData = {
  token: string,
  password: string,
}

export type ListUserParams = {
  offset: number;
  limit: number;
  name?: string;
  email?: string;
  role?: string;
  phone?: string;
  active?: boolean;
}

export type UserWithPages = {
  total: number,
  users: User[]
}

export function getUsers(data: ListUserParams) {
  const params = new URLSearchParams();

  params.set('offset', String(data.offset));
  params.set('limit', String(data.limit));
  if (data.name)
    params.set('name', String(data.name));
  if (data.active)
    params.set('active', String(data.active));
  if (data.email)
    params.set('email', String(data.email));
  if (data.phone)
    params.set('phone', String(data.phone));
  if (data.role)
    params.set('role', String(data.role));

  const paramString = params.toString();
  return apiFetch<UserWithPages>(`/api/users${paramString ? "?" + paramString : ""}`, { clientAware: true });
}

export function getUser(identifier: string) {
  return apiFetch<User>(`/api/users/${identifier}`);
}

export function getUserClients(identifier: string) {
  return apiFetch<Client[]>(`/api/users/${identifier}/clients`);
}

export interface UserClientModule {
  module_id: string;
}

export function getModulesByUserAndClient(userId: string, clientId: string) {
  return apiFetch<UserClientModule[]>(`/api/users/${userId}/clients/${clientId}/modules`);
}

// User Company
export function getUserCompanies(identifier: string) {
  return apiFetch<Company[]>(`/api/users/${identifier}/companies`);
}

export function createUserCompany(data: CreateUserCompanyData) {
  return apiFetch<UserCompany>(`/api/user-companies`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deleteUserCompany(data: CreateUserCompanyData) {
  return apiFetch(`/api/users/${data.user_id}/companies/${data.company_id}`, {
    method: "DELETE"
  });
}

// User Company Module
export function getModulesByUserCompany(data: CreateUserCompanyData) {
  return apiFetch<UserCompanyModule[]>(`/api/users/${data.user_id}/companies/${data.company_id}/modules/`);
}

export function createUserCompanyModule(data: UserCompanyModuleData) {
  return apiFetch<UserCompanyModule>(`/api/users/${data.user_id}/companies/${data.company_id}/modules/${data.module_id}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deleteUserCompanyModule(data: UserCompanyModuleData) {
  return apiFetch<UserCompanyModule>(`/api/users/${data.user_id}/companies/${data.company_id}/modules/${data.module_id}`, {
    method: "DELETE"
  });
}

// User
export function createUser(data: CreateUserData) {
  return apiFetch<User>("/api/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateUser(id: string, data: Partial<User>) {
  return apiFetch<User>(`/api/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteUser(id: string) {
  return apiFetch<void>(`/api/users/${id}`, {
    method: "DELETE",
  });
}

export function sendForgotPasswordEmail(email: string) {
  return apiFetch<void>(`/forgot-password/${email}`, {
    method: "POST"
  });
}

export function resetPassword(data: ResetPasswordData) {
  return apiFetch<void>(`/reset-password`, {
    method: "POST",
    body: JSON.stringify(data)
  });
}

// API tokens (contas de serviço, só admin)
export interface ApiToken {
  id: string;
  name: string;
  expires_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

export type CreateApiTokenData = {
  name: string;
  expires_at?: string;
}

export function getApiTokens(userId: string) {
  return apiFetch<ApiToken[]>(`/api/users/${userId}/api-tokens`);
}

// O token em texto puro só vem nesta resposta.
export function createApiToken(userId: string, data: CreateApiTokenData) {
  return apiFetch<ApiToken & { token: string }>(`/api/users/${userId}/api-tokens`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function revokeApiToken(userId: string, tokenId: string) {
  return apiFetch<void>(`/api/users/${userId}/api-tokens/${tokenId}`, {
    method: "DELETE",
  });
}
